import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { apiClient } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Building2, Activity, AlertTriangle, TrendingUp, Sparkles, DollarSign, Users } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis } from "recharts";

// Manual sizer: measures container with ResizeObserver and only renders the
// chart with explicit pixel width/height. Avoids Recharts ResponsiveContainer's
// dev-mode `width(-1)/height(-1)` warning that fires on first paint inside
// CSS grid layouts.
const SizedChart = ({ children, height = 220 }) => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const update = () => setWidth(el.clientWidth || 0);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ width: "100%", height, minWidth: 0 }}>
      {width > 0 ? children({ width, height }) : null}
    </div>
  );
};

const AdminOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get("/admin/stats");
        setStats(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <AdminLayout><div className="p-10 text-center text-slate-400">Loading…</div></AdminLayout>;
  if (!stats) return <AdminLayout><div className="p-10 text-red-500">Failed to load admin stats.</div></AdminLayout>;

  const planColors = { free: "#94a3b8", starter: "#3b82f6", pro: "#f59e0b" };
  const planChartData = Object.entries(stats.plan_distribution || {}).map(([id, value]) => ({ id, name: id.charAt(0).toUpperCase() + id.slice(1), value, fill: planColors[id] }));

  const Stat = ({ icon, label, value, sub, accent = "text-[#0A192F]" }) => (
    <Card className="border border-slate-200">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-1">{label}</p>
            <p className={`text-3xl font-bold tracking-tight ${accent}`}>{value}</p>
            {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
          </div>
          <div className="p-2.5 bg-slate-50 rounded-lg">{icon}</div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-[#0A192F] tracking-tight font-['Manrope']">Platform Overview</h1>
          <p className="text-sm text-slate-500 mt-1">Health, growth and revenue of every Nirvanta tenant.</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={<Building2 className="w-5 h-5 text-[#0D9488]" />} label="Total Tenants" value={stats.total_tenants} />
          <Stat icon={<Activity className="w-5 h-5 text-[#10B981]" />} label="Active (7d)" value={stats.active_tenants_7d} sub="had time entries this week" />
          <Stat icon={<TrendingUp className="w-5 h-5 text-blue-500" />} label="New Signups (7d)" value={stats.new_signups_7d} accent="text-blue-600" />
          <Stat icon={<AlertTriangle className="w-5 h-5 text-red-500" />} label="Suspended" value={stats.suspended_tenants} accent={stats.suspended_tenants > 0 ? "text-red-600" : "text-[#0A192F]"} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Stat icon={<DollarSign className="w-5 h-5 text-amber-500" />} label="MRR (Monthly Recurring Revenue)" value={`$${stats.mrr_usd.toLocaleString()}`} sub={`from ${stats.plan_distribution.starter} Starter + ${stats.plan_distribution.pro} Pro`} accent="text-amber-600" />
          <Stat icon={<Sparkles className="w-5 h-5 text-purple-500" />} label="Paid Transactions (30d)" value={stats.paid_transactions_30d} accent="text-purple-600" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="border border-slate-200 min-w-0">
            <CardContent className="p-5 min-w-0">
              <p className="text-sm font-semibold text-[#0A192F] mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> Plan Distribution</p>
              <SizedChart>
                {({ width, height }) => (
                  <PieChart width={width} height={height}>
                    <Pie data={planChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} innerRadius={45} paddingAngle={2}>
                      {planChartData.map(p => <Cell key={p.id} fill={p.fill} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                )}
              </SizedChart>
              <div className="flex justify-around mt-2 text-xs">
                {planChartData.map(p => (
                  <div key={p.id} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.fill }} />
                    <span className="font-medium text-[#0A192F] capitalize">{p.name}</span>
                    <span className="text-slate-400">{p.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-200 min-w-0">
            <CardContent className="p-5 min-w-0">
              <p className="text-sm font-semibold text-[#0A192F] mb-3">Plan Counts</p>
              <SizedChart>
                {({ width, height }) => (
                  <BarChart width={width} height={height} data={planChartData}>
                    <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
                    <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                      {planChartData.map(p => <Cell key={p.id} fill={p.fill} />)}
                    </Bar>
                  </BarChart>
                )}
              </SizedChart>
            </CardContent>
          </Card>
        </div>

        <Card className="border border-slate-200 bg-slate-50/40">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-[#0A192F]">Manage tenants</p>
              <p className="text-xs text-slate-500">View, change plans, suspend, and impersonate any tenant.</p>
            </div>
            <a href="/admin/tenants" className="text-sm font-semibold text-[#0D9488] hover:underline" data-testid="overview-go-tenants">Go to Tenants →</a>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminOverview;
